/**
 * What a message's menu offers, as data.
 *
 * The desktop's `showMessageMenu` (`relay/src/ui/menus.cpp`), entry for entry
 * and in its order: four reactions, then the actions before the utilities —
 * replying is what somebody opened the menu to do, copying is what they came
 * to fetch — then edit and delete, which are the author's. Kept apart from the
 * component so a test can read it without a DOM.
 *
 * "Add reaction…" is the one entry missing: it opens the desktop's picker, and
 * this app has no picker to open.
 *
 * "Translate" is this app's own — the desktop does not have it yet — and sits
 * beside copying as a utility, offered wherever the deployment can translate
 * and the message has a body. In Mauvely's own conversation it is the one
 * thing beside copying: an announcement is as likely as anything to arrive in
 * a language its reader does not read.
 */

/** The menu's reactions: the hover bar's three and one more, as the desktop's. */
export const MENU_REACTIONS = ['\u{1F44D}', '\u{2764}', '\u{1F602}', '\u{1F440}'];

export type MenuEntry =
  | { kind: 'react'; emoji: string }
  | { kind: 'reply' | 'thread' | 'copy' | 'translate' | 'edit' | 'delete' }
  | { kind: 'separator' };

export function messageMenu(state: {
  /** Your own message. Edit and delete are offered for nothing else, and every
   *  receiver refuses them for anything else again. */
  mine: boolean;
  /** A row inside the thread pane. Threads are one level deep, so it does not
   *  offer to start another. */
  inThread: boolean;
  /** Mauvely's own conversation: copying and translating are all there is to
   *  do. */
  readOnly: boolean;
  /** Whether this deployment can translate, the message has a body within the
   *  server's cap, and nothing is already drawn for it. */
  translatable?: boolean;
}): MenuEntry[] {
  if (state.readOnly) {
    return state.translatable ? [{ kind: 'copy' }, { kind: 'translate' }] : [{ kind: 'copy' }];
  }
  const out: MenuEntry[] = MENU_REACTIONS.map((emoji) => ({ kind: 'react', emoji }));
  out.push({ kind: 'separator' }, { kind: 'reply' });
  if (!state.inThread) out.push({ kind: 'thread' });
  out.push({ kind: 'separator' }, { kind: 'copy' });
  if (state.translatable) out.push({ kind: 'translate' });
  if (state.mine) out.push({ kind: 'separator' }, { kind: 'edit' }, { kind: 'delete' });
  return out;
}
